import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  cloneCampaign,
  createCampaign,
  deleteCampaign,
  enableCampaign,
  fetchCampaigns,
  selectCampaign,
  updateCampaign,
} from "actions/personalization";

import cx from "classnames";
import _, { isEmpty } from "lodash";

import {
  Row,
  Col,
  Dropdown,
  Button,
  Form,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { toast } from "react-toastify";

import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import StoryboardContext from "components/Storyboard/context";
import { DeletePersonalizationModal } from "../..";
import InfiniteScroll from "components/InfiniteScroll";
import { FailureToast, SuccessToast } from "components/Toast";

import styles from "./campaignsList.module.scss";


export const CampaignList = () => {
  const dispatch = useDispatch();

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardId = useSelector((state) =>
    _.get(
      state,
      "storyboards.storyboard.item.id",
      _.get(storyboardContext, "id")
    )
  );
  const campaigns = useSelector((state) => _.get(state, "personalization.campaigns.items", [])) || [];

  const selectedCampaign = useSelector((state) => {
    const selectedId = _.get(state, "personalization.campaigns.selectedCampaign", null);
    const campaigns = _.get(state, "personalization.campaigns.items", []);
    
    return campaigns.find((campaign) => campaign.id === selectedId) || null;
  });

  const loading = useSelector((state) => _.get(state, "personalization.campaigns.loading")) ||false;

  const pagination = useSelector((state) => _.get(state, "personalization.campaigns.pagination", {}));

  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const updating = useSelector((state) => _.get(state, "personalization.campaigns.updating")) || false;

  const error = useSelector((state) => _.get(state, "personalization.campaigns.error")) || null;

  const creating =
    useSelector((state) =>
      _.get(state, "personalization.campaigns.creating")
    ) || false;

  const [isExpanded, setExpanded] = useState(false);
  const [updatedValue, setUpdatedValue] = useState(null);

  const [selectedSortFilters, setSelectedSortFilters] = useState({
    sortType: "modified_date",
    sortOrder: "DESC",
  });

  const sortFilterTypeOption = [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Date",
      value: "modified_date",
    },
  ];

  useEffect(() => {
    if (!storyboardId) {
      return;
    }
    // Fetch campaigns or any initial data here if needed
    handleFetchCampaigns(0);
  }, [selectedSortFilters]);

  const handleFetchCampaigns = (page) => {
    if (!storyboardId) {
      return;
    }

    const request = {
      storyboard: storyboardId,
      sort: selectedSortFilters.sortType,
      order: selectedSortFilters.sortOrder,
    };

    dispatch(fetchCampaigns(request, page, 10));
  };

  const activeDropdownLabel = _.find(sortFilterTypeOption, (data) => {
    return data.value == selectedSortFilters.sortType;
  });

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchCampaigns(nextPage);
  };

  useEffect(() => {
    if (!selectedCampaign) {
      const activeCampaign = _.get(campaigns[0], 'id', null);
      dispatch(selectCampaign(activeCampaign));
    }

    handleReset();
  }, [selectedCampaign]);

  const handleCreateCampaign = () => {
    const request = {
      name: "New Campaign",
      enabled: false,
      sort: selectedSortFilters.sortType,
      order: selectedSortFilters.sortOrder,
      storyboard: storyboardId,
    };

    dispatch(createCampaign(request));
  };

  const handleRename = (value) => {
    if (!updatedValue || value.id !== updatedValue?.id) {
      setUpdatedValue((prev)=>{
        return {
          ...prev,
          name: value.name,
        };
      });
    }
  };

  const handleReset = () => {
    if (updatedValue) {
      setUpdatedValue(null);
    }
  };

  const handleToggleEnable = (value) => {
    if ( _.isEqual(value, updatedValue?.enabled)) {
      return;
    }

    setUpdatedValue((prev)=>{
      return {
        ...prev,
        enabled: value,
      };
    });
  }

  const handleUpdateCampaign = () => {
    if (!updatedValue) {
      return;
    }

    const request = {
      id: selectedCampaign.id,
      ...updatedValue,
      storyboard: storyboardId,
    };
    

    dispatch(
      updateCampaign(request, (type) => {
        setUpdatedValue(null);
        switch (type) {
          case "success":
            toast(
              <SuccessToast
                heading={`Success`}
                description="campaign updated successfully"
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
              }
            );
            break;
          case "error":
            toast(
              <FailureToast
                heading={`Failure`}
                description="campaign updated Failed"
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
              }
            );
            break;
        }
      })
    );
  };

  const onFilterChange = (type, value) => {
    setSelectedSortFilters((state) => {
      return {
        ...state,
        [type]: value,
      };
    });
  };

  return (
    <div
      className={cx(styles.campaignColumn, {
        [styles.expanded]: isExpanded,
      })}
    >
      {/* Campaign content */}
      <div className={styles.header}>
        <span className={cx(styles.title)}>List of Campaign</span>
        <Button
          className={styles.expandButton}
          onClick={() => setExpanded((prev) => !prev)}
          variant="info"
        >
          <i className={cx(styles.icon, "cleverstory-right-align")} />
        </Button>
      </div>
      <div className={styles.secondaryHeader}>
        <div className={styles.conditions}>
          <span className={styles.label}>
            <label className={styles.filterLabel}>
              {selectedSortFilters.sortOrder && (
                <img
                  onClick={() =>
                    onFilterChange(
                      "sortOrder",
                      selectedSortFilters.sortOrder === "ASC" ? "DESC" : "ASC"
                    )
                  }
                  className={cx(
                    styles.sortIcon,
                    selectedSortFilters.sortOrder === "ASC"
                      ? styles.ascending
                      : styles.descending
                  )}
                  alt="sortIcon"
                  src="/images/sortIcon.svg"
                />
              )}
            </label>
          </span>

          <Dropdown className={styles.sortDropdown}>
            <Dropdown.Toggle className={styles.selected}>
              <Button size="sm" variant="primary" className={styles.button}>
                <span className={styles.filterType}>
                  {activeDropdownLabel.label}
                </span>
                <i className={cx(styles.icon, "cleverstory-drop-down")} />
              </Button>
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.typesMenu}>
              {sortFilterTypeOption.map((option, index) => {
                return (
                  <Dropdown.Item
                    active={option.value == selectedSortFilters.sortType}
                    onClick={() => onFilterChange("sortType", option.value)}
                    key={index}
                    eventKey={index}
                  >
                    {option.label}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <Button
          onClick={handleCreateCampaign}
          variant="primary"
          disabled={creating}
          className={styles.createButton}
        >
          <i className={cx(styles.icon, "cleverstory-plus")} />
          <span className={cx(styles.text)}>
            {creating ? "Creating.." : "Create Campaign"}
          </span>
        </Button>
      </div>
      <InfiniteScroll
        className={styles.campaignContent}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={loadMore}
      >
        {campaigns.map((campaign) => {
          return(
            <CampaignCardView
            campaign={campaign}
            handleToggleEnable={handleToggleEnable}
            activeCampaign={selectedCampaign}
            updatedValue={updatedValue}
            handleSelection={()=>{
              dispatch(selectCampaign(campaign.id));
              if (updatedValue && updatedValue.id !== campaign.id) {
                setUpdatedValue(null);
              }
            }}
            handleRename={handleRename}
            isExpanded={isExpanded}
          />
          )
        })}
      </InfiniteScroll>
      <footer className={styles.userManagementFooter}>
        <div className={styles.footerAction}>
          <Button
            variant="secondary"
            onClick={handleReset}
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={updating || !updatedValue}
            onClick={() => {
              handleUpdateCampaign();
            }}
            className={styles.saveButton}
          >
            {updating ? "Saving.." : "Save Changes"}
          </Button>
        </div>
      </footer>
    </div>
  );
};

const CampaignCardView = React.memo(
  ({
    campaign,
    activeCampaign,
    handleToggleEnable,
    updatedValue,
    handleRename,
    handleSelection,
    isExpanded,
  }) => {
    const dispatch = useDispatch();

    const inputRef = React.useRef(null);

    const [state, setState] = useState({
      errorMessage: "",
      rename: false,
      deleteModal: false,
      name: "",
    });

    useEffect(()=>{
      setState((prev) => {
        return {
          ...prev,
          name: campaign.name || "",
        };
      })
    }, [campaign]);

    const deleting =
      useSelector((state) =>
        _.get(state, "personalization.campaigns.deleting")
      ) || false;

    const segmentItems = useSelector((state) => _.get(state, `personalization.segments.data.${activeCampaign?.id}.items`, [])) || [];

    const renderInitials = (name) => {
      let firstName = name || "";

      let initial = "";

      if (name) {
        initial = firstName.charAt(0);
      }

      return initial.toUpperCase();
    };

    const onSelect = () => {
      if (!activeCampaign || activeCampaign?.id !== campaign.id) {
        handleSelection("campaign", campaign);
      }
    };

    useEffect(() => {
      if (activeCampaign?.id) {
        handleResetValues();
      }
    }, [activeCampaign?.id]);

    useEffect(() => {
      if (_.isEmpty(updatedValue)) {
        handleResetValues();
      }
    }, [updatedValue]);

    const showDeleteModal = (value) => {
      setState((prev) => {
        return {
          ...prev,
          deleteModal: value,
        };
      });
    };

    const handleDelete = () => {
      dispatch(
        deleteCampaign(activeCampaign.id, () => {
          setState((prev) => {
            return {
              ...prev,
              deleteModal: false,
            };
          });
          handleSelection("campaign", null);
        })
      );
    };

    const handleSaveChanges = () => {
      if (!state.name || state.name.trim() === "") {
        setState((prev) => ({
          ...prev,
          errorMessage: "Please provide a valid name",
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        rename: !state.rename,
        errorMessage: "",
      }));

      handleRename({ id: activeCampaign?.id, name: state.name });
    };

    const handleClone = () => {

      if (activeCampaign?.id !== campaign.id) {
        return;
      }

      dispatch(cloneCampaign(campaign?.id, (type)=>{
        switch (type) {
          case "success":
            handleSelection("campaign", null);
            toast(
              <SuccessToast
                heading={`Success`}
                description="Campaign cloned successfully"
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
              }
            );
            break;
          case "error":
            toast(
              <FailureToast
                heading={`Failure`}
                description="Campaign clone Failed"
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
              }
            );
            break;
        }
      }))
    }

    const handleResetValues = () => {
      if (campaign.name !== state.name || activeCampaign?.id !== campaign.id) {
        setState((prev) => {
          return {
            ...prev,
            rename: false,
            errorMessage: "",
            name: campaign.name || "",
          };
        });
        return;
      }
    };

    useEffect(() => {
      if (!isExpanded) {
        setState((prev) => {
          return {
            ...prev,
            rename: false,
          };
        });
      }
    }, [isExpanded]);

    useEffect(() => {
      if (state.rename) {
        inputRef.current.focus();
      }
    }, [state.rename]);

    const popover = (
      <Popover
        key={campaign.id}
        bsPrefix="popover"
        className={styles.campaignSettingsPopover}
      >
        <Popover.Body className={styles.campaignSettingsPopoverBody}>
          <div className={styles.campaignSettingsPopoverContent}>
            <span
              onClick={() => {
                setState((prev) => {
                  return {
                    ...prev,
                    rename: true,
                  };
                });
              }}
            >
              Rename
            </span>
            <span onClick={handleClone} >Clone</span>
            <span
              onClick={() => {
                showDeleteModal(true);
              }}
            >
              Delete
            </span>
          </div>
        </Popover.Body>
      </Popover>
    );

    return (
      <div
        key={campaign.id}
        id={campaign.id}
        onClick={onSelect}
        className={cx(styles.campaignItem, {
          [styles.active]: campaign?.id == activeCampaign?.id,
        })}
      >
        {!isExpanded && campaign?.enabled &&
          <div className={styles.enabledCampaignHighlighter}></div>
        }
        {state.rename && (
          <Form.Group className={styles.siteNameFormGroup}>
            <Form.Control
              type="text"
              ref={inputRef}
              value={state.name}
              onChange={(e) => {
                setState((prev) => {
                  return {
                    ...prev,
                    name: e.target.value,
                  };
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveChanges();
                }
              }}
              onBlur={() => {
                handleSaveChanges();
              }}
              isInvalid={state.errorMessage}
            />
          </Form.Group>
        )}
        {!state.rename && (
          <span className={styles.name}>
            {isExpanded ? (
              <TruncatedTextWithTooltip
                content={state.name}
                maxWidth={140}
                placement="top"
              />
            ) : (
              renderInitials(state.name)
            )}
          </span>
        )}

        <div className={styles.actionContainer}>
          <Form.Switch id={campaign.id} className={cx(styles.toggle)}>
            <Form.Switch.Input
              onClick={()=>{handleToggleEnable(updatedValue?.enabled !== undefined ? !updatedValue?.enabled : !campaign.enabled)}}

              checked={ updatedValue && updatedValue?.enabled !== undefined && activeCampaign?.id == campaign.id ?
                updatedValue?.enabled : activeCampaign?.id !== campaign.id && updatedValue && updatedValue?.enabled ? false : campaign.enabled}

              disabled={activeCampaign?.id !== campaign.id || isEmpty(segmentItems)}
              id={campaign.id}
              isInvalid
            />
          </Form.Switch>
          {activeCampaign?.id === campaign.id && (
            <OverlayTrigger
              rootClose={true}
              rootCloseEvent="mousedown"
              key={campaign.id}
              trigger="click"
              placement="bottom"
              overlay={popover}
            >
              <i className={cx("cleverstory-more", styles.iconMore)} />
            </OverlayTrigger>
          )}
        </div>
        <DeletePersonalizationModal
          deleting={deleting}
          deleteModal={state.deleteModal}
          showDeleteModal={showDeleteModal}
          onDelete={handleDelete}
        />
      </div>
    );
  }
);